import type {
  ContentType,
  SearchAggregations,
} from "../../api/types";
import { TYPE_LABELS } from "../../lib/format";
import { useCollections, collectionLabel } from "../../hooks/useCollections";
import { Tag } from "../Tag";

/**
 * Faceted filter panel. Renders directly from the aggregations the backend
 * returns, so the available facets always reflect the current result set. A
 * type that has no hits for the current query simply doesn't appear.
 *
 * Site-wide search deliberately covers every collection, including the ones
 * with their own section. The Collection facet is what keeps that usable: a
 * search for "beach" can be narrowed to Life in one click rather than being
 * read past a thousand dive frames.
 */
interface FacetsProps {
  aggregations: SearchAggregations | null;
  selectedTypes: ContentType[];
  selectedTags: string[];
  selectedCollections: string[];
  selectedYear: string | null;
  onToggleType: (type: ContentType) => void;
  onToggleTag: (tag: string) => void;
  onToggleCollection: (collection: string) => void;
  onSelectYear: (year: string | null) => void;
  onClear: () => void;
}

export function Facets({
  aggregations,
  selectedTypes,
  selectedTags,
  selectedCollections,
  selectedYear,
  onToggleType,
  onToggleTag,
  onToggleCollection,
  onSelectYear,
  onClear,
}: FacetsProps) {
  const byType = aggregations?.byType ?? [];
  const byTag = aggregations?.byTag ?? [];
  const byCollection = aggregations?.byCollection ?? [];
  const byYear = aggregations?.byYear ?? [];
  const { collections } = useCollections();

  const hasFilters =
    selectedTypes.length > 0 ||
    selectedTags.length > 0 ||
    selectedCollections.length > 0 ||
    selectedYear !== null;

  return (
    <aside className="facets" aria-label="Filters">
      <div className="facets__group">
        <p className="facets__legend">
          <span>Type</span>
          {hasFilters && (
            <button
              type="button"
              className="searchbar__clear"
              style={{ padding: 0, fontSize: "0.66rem" }}
              onClick={onClear}
            >
              Reset
            </button>
          )}
        </p>

        {byType.length === 0 && (
          <p className="facet-row__count">No results to filter.</p>
        )}

        {byType.map((facet) => {
          const type = facet.value as ContentType;
          const checked = selectedTypes.includes(type);
          return (
            <label key={facet.value} className="facet-row">
              <span className="facet-row__label">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggleType(type)}
                />
                {TYPE_LABELS[type] ?? facet.value}
              </span>
              <span className="facet-row__count">{facet.count}</span>
            </label>
          );
        })}
      </div>

      {byCollection.length > 1 && (
        <div className="facets__group">
          <p className="facets__legend">
            <span>Collection</span>
          </p>
          {byCollection.map((facet) => (
            <label key={facet.value} className="facet-row">
              <span className="facet-row__label">
                <input
                  type="checkbox"
                  checked={selectedCollections.includes(facet.value)}
                  onChange={() => onToggleCollection(facet.value)}
                />
                {collectionLabel(collections, facet.value)}
              </span>
              <span className="facet-row__count">{facet.count}</span>
            </label>
          ))}
        </div>
      )}

      {byTag.length > 0 && (
        <div className="facets__group">
          <p className="facets__legend">
            <span>Tags</span>
          </p>
          <div className="facets__tags">
            {byTag.map((facet) => (
              <Tag
                key={facet.value}
                label={facet.value}
                count={facet.count}
                active={selectedTags.includes(facet.value)}
                onClick={() => onToggleTag(facet.value)}
              />
            ))}
          </div>
        </div>
      )}

      {byYear.length > 1 && (
        <div className="facets__group">
          <p className="facets__legend">
            <span>Year</span>
          </p>
          <div className="facets__tags">
            {byYear.map((facet) => (
              <Tag
                key={facet.value}
                label={facet.value}
                count={facet.count}
                active={selectedYear === facet.value}
                onClick={() =>
                  onSelectYear(selectedYear === facet.value ? null : facet.value)
                }
              />
            ))}
          </div>
        </div>
      )}
    </aside>
  );
}
